import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { SkillShort } from '~/shared/data-contract/skill';
import type { LanguageCode } from '~/shared/internationalization';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

type SkillWithRequiredId = Partial<SkillShort> & { readonly id: string };

type UseRelatedSkillsOptions = {
    readonly acceptLanguage: LanguageCode;
    readonly skills: readonly SkillWithRequiredId[];
};

export const useRelatedSkills = ({
    acceptLanguage,
    skills,
}: UseRelatedSkillsOptions): UseQueryResult<readonly SkillShort[], Error> => {
    const { fetchRelatedSkills } = useApplicationFetchers();

    return useQuery({
        enabled: skills.length > 0,
        queryFn: async () => {
            const result = await fetchRelatedSkills({
                acceptLanguage,
                skills,
            });

            if (result.isErr === true) {
                throw result.error;
            }

            return result.value;
        },
        queryKey: jobArchitectureKeys.relatedSkills(skills, acceptLanguage),
        throwOnError: false,
    });
};
